import React, { useEffect, useState } from "react";
import { PLANS } from "@/config/stripe";
import { getProductById, getPriceById } from "@/services/stripe";
import {
  STRIPE_SEMIANNUAL_PRODUCT_ID,
  STRIPE_ANNUAL_PRODUCT_ID,
  STRIPE_SEMIANNUAL_PRICE_ID,
  STRIPE_ANNUAL_PRICE_ID,
} from "@/config/env";

interface PlanSummaryProps {
  planType: "MONTHLY" | "YEARLY" | "SEMIANNUAL" | null;
}

const PlanSummary: React.FC<PlanSummaryProps> = ({ planType }) => {
  const [priceData, setPriceData] = useState({
    discountedPrice: 0,
    originalPrice: 0,
    planName: "",
  });

  useEffect(() => {
    if (!planType || !PLANS[planType]) return;

    const plan = PLANS[planType];

    // Get the current price data from the same source as the pricing page
    if (planType === "SEMIANNUAL") {
      const semiannualPrice = getPriceById(STRIPE_SEMIANNUAL_PRICE_ID);
      const semiannualProduct = getProductById(STRIPE_SEMIANNUAL_PRODUCT_ID);

      // Get the base price
      const basePrice = semiannualPrice?.unitAmount
        ? semiannualPrice.unitAmount / 100
        : PLANS.SEMIANNUAL.price;

      // Use exactly the same values as shown on pricing page
      // $12.50 for discounted price and $25.99 for original price
      setPriceData({
        discountedPrice: 12.5,
        originalPrice: 25.99,
        planName: semiannualProduct?.name || plan.name,
      });
    } else if (planType === "YEARLY") {
      const annualPrice = getPriceById(STRIPE_ANNUAL_PRICE_ID);
      const annualProduct = getProductById(STRIPE_ANNUAL_PRODUCT_ID);

      // Get the base price
      const basePrice = annualPrice?.unitAmount
        ? annualPrice.unitAmount / 100
        : PLANS.YEARLY.price;

      // Use exactly the same values as shown on pricing page
      // $20.00 for discounted price and $40.99 for original price
      setPriceData({
        discountedPrice: 20.0,
        originalPrice: 40.99,
        planName: annualProduct?.name || plan.name,
      });
    }
  }, [planType]);

  if (!planType || !PLANS[planType]) {
    return (
      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-yellow-700">
          No plan selected or invalid plan type. Please return to pricing page.
        </p>
      </div>
    );
  }

  const plan = PLANS[planType];

  return (
    <div className="mb-6">
      <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 mb-4 text-sm text-indigo-800">
        <p className="font-medium">✨ Beta Launch Special Offer</p>
        <p>
          You've qualified for our early adopter discount - 50% off and extended
          14-day trial!
        </p>
      </div>

      <p className="text-lg mb-2">
        You selected the{" "}
        <span className="font-semibold">{priceData.planName || plan.name}</span>{" "}
        plan
      </p>
      <p className="text-gray-600 mb-1">
        <span className="font-medium text-green-600">
          ${priceData.discountedPrice.toFixed(2)}
        </span>
        <span className="text-gray-400 line-through ml-2">
          ${priceData.originalPrice.toFixed(2)}
        </span>
        per{" "}
        {planType === "MONTHLY"
          ? "month"
          : planType === "YEARLY"
            ? "year"
            : "semi-annual"}
      </p>
      <p className="text-gray-500 text-sm mb-4">
        Includes 14-day free trial. Credit card required, you'll be charged
        after the trial ends.
      </p>

      <div className="border border-blue-100 bg-blue-50 rounded-md p-4">
        <h3 className="text-sm font-medium text-blue-800 mb-2">
          Plan includes:
        </h3>
        <ul className="list-disc pl-5 space-y-1">
          {plan.features.map((feature, index) => (
            <li key={index} className="text-sm text-blue-700">
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default PlanSummary;
